/**
 * Per-tab visibility of filters (DSA Pricing-Brand).
 *
 * ``DashboardFilter.tabs`` names the dashboard tabs a filter is SHOWN
 * on; empty/absent = every tab (every pre-existing report). It is
 * render-only: the engine applies a hidden filter to the query exactly
 * as before — the host just does not draw the control (nor its chip)
 * while another tab is active, so a filter only one tab's elements read
 * (the coverage of a matrix, the retailers of an inspector) does not
 * clutter the rest.
 *
 * Both hosts (portal page, builder preview) and both filter surfaces
 * (banner bar, collapsible header) resolve through THESE functions, so
 * what is hidden where is one decision — and the tab it is decided
 * against is the one ``DashboardBody`` renders, through the same
 * fallback (``resolveActiveTabId``).
 */
import type { DashboardFilter, DashboardTab } from "./types";

/**
 * The tab the body renders for a requested id: the request when it
 * names a tab, else the FIRST tab; ``null`` for a dashboard without
 * tabs. This is the fallback ``DashboardBody`` applies (a stale
 * ``?tab=`` never renders an empty page) — hosts resolve the tab their
 * filter bar hides against with the same call.
 */
export function resolveActiveTabId(
  tabs: ReadonlyArray<Pick<DashboardTab, "id">> | null | undefined,
  requested: string | null | undefined,
): string | null {
  if (!tabs || tabs.length === 0) return null;
  const hit =
    requested != null ? tabs.find((tab) => tab.id === requested) : undefined;
  return (hit ?? tabs[0]!).id;
}

/**
 * Does this filter render while ``activeTab`` is current? Always when
 * the filter names no tabs, and always when there is no current tab (a
 * dashboard without tabs has nothing to hide behind).
 */
export function isFilterOnTab(
  filt: Pick<DashboardFilter, "tabs">,
  activeTab: string | null | undefined,
): boolean {
  if (activeTab == null) return true;
  const tabs = filt.tabs;
  if (!tabs || tabs.length === 0) return true;
  return tabs.includes(activeTab);
}

/**
 * The filters to draw on ``activeTab``, in authoring order. Returns the
 * SAME array when nothing is hidden, so memoised consumers keep their
 * identity (and every caller without tabs stays byte-identical).
 */
export function filtersForTab<F extends Pick<DashboardFilter, "tabs">>(
  filters: ReadonlyArray<F>,
  activeTab: string | null | undefined,
): ReadonlyArray<F> {
  if (activeTab == null) return filters;
  const visible = filters.filter((f) => isFilterOnTab(f, activeTab));
  return visible.length === filters.length ? filters : visible;
}
